import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useSelectedOrganizationId } from "@/organization/api";
import { useOrganizationInviteCodes, useCreateInviteCode, useDeleteInviteCode } from "@/organization/invite-codes-api";
import { ThaloDataTable } from "@/components/thalo-data-table";
import { ColumnDef } from "@tanstack/react-table";
import { OrganizationInviteCode } from "@/organization/invite-codes";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Trash2, Plus } from "lucide-react";
import { format } from "date-fns";

const InviteCodesTableColumns = (
  deleteInviteCode: (inviteCodeId: number, code: string) => void
): ColumnDef<OrganizationInviteCode>[] => [
  {
    id: "code",
    accessorKey: "code",
    enableSorting: false,
    header: "Invite Code",
    cell: ({ row }) => {
      return (
        <div className="font-mono font-medium">
          {row.original.code}
        </div>
      );
    },
  },
  {
    id: "created_by",
    accessorFn: (row) => row.created_by ? `${row.created_by.first_name} ${row.created_by.last_name}` : "Unknown",
    enableSorting: false,
    header: "Created By",
    cell: ({ row }) => {
      const createdBy = row.original.created_by;
      if (!createdBy) {
        return <span className="text-muted-foreground">Unknown</span>;
      }
      return (
        <div className="flex flex-col">
          <div className="font-medium">
            {createdBy.first_name} {createdBy.last_name}
          </div>
          <div className="text-muted-foreground text-sm">
            {createdBy.username}
          </div>
        </div>
      );
    },
  },
  {
    id: "times_used",
    accessorKey: "times_used",
    enableSorting: false,
    header: "Times Used",
    cell: ({ row }) => row.original.times_used,
  },
  {
    id: "created_at",
    accessorKey: "created_at",
    enableSorting: false,
    header: "Created",
    cell: ({ row }) => {
      return format(new Date(row.original.created_at), "MMM d, yyyy");
    },
  },
  {
    id: "last_used_at",
    accessorKey: "last_used_at",
    enableSorting: false,
    header: "Last Used",
    cell: ({ row }) => {
      if (!row.original.last_used_at) {
        return <span className="text-muted-foreground">Never</span>;
      }
      return format(new Date(row.original.last_used_at), "MMM d, yyyy");
    },
  },
  {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => {
      const code = row.original.code;

      return (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="ghost" size="icon">
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete invite code?</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete the invite code "{code}"?
                This action cannot be undone and the code will no longer work.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => deleteInviteCode(row.original.id, code)}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      );
    },
  },
];

const InviteCodesPage = () => {
  const organizationId = useSelectedOrganizationId();
  const { data, isLoading } = useOrganizationInviteCodes(organizationId);
  const createInviteCode = useCreateInviteCode();
  const deleteInviteCode = useDeleteInviteCode();

  const handleCreateInviteCode = () => {
    createInviteCode.mutate({ organizationId });
  };

  const handleDeleteInviteCode = (inviteCodeId: number, code: string) => {
    deleteInviteCode.mutate({ inviteCodeId, organizationId });
  };

  return (
    <Card className="">
      <div className="flex items-center justify-between p-3 lg:p-4">
        <h2 className="font-semibold text-lg">Invite Codes</h2>
        <Button onClick={handleCreateInviteCode} disabled={createInviteCode.isPending}>
          <Plus className="h-4 w-4 mr-2" />
          Generate New Code
        </Button>
      </div>
      <div className="col-span-12 lg:col-span-12">
        <ThaloDataTable
          variant="thalo"
          data={data?.results || []}
          columns={InviteCodesTableColumns(handleDeleteInviteCode)}
          isLoading={isLoading}
          pageCount={1}
          itemsPerPage={100}
          showColumnFilter={false}
        />
      </div>
    </Card>
  );
};

export default InviteCodesPage;
